var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const clamp0to1 = (x) => Math.max(0, Math.min(1, x));
class Point {
  constructor(x, y) {
    __publicField(this, "_x");
    __publicField(this, "_y");
    __publicField(this, "eventTarget", new EventTarget());
    this.x = x;
    this.y = y;
  }
  get x() {
    return this._x;
  }
  set x(newX) {
    this._x = clamp0to1(newX);
    this.dispatchUpdatedEvent();
  }
  get y() {
    return this._y;
  }
  set y(newY) {
    this._y = clamp0to1(newY);
    this.dispatchUpdatedEvent();
  }
  setPosition(x, y) {
    this.x = x;
    this.y = y;
    this.dispatchUpdatedEvent();
  }
  dispatchUpdatedEvent() {
    this.eventTarget.dispatchEvent(new CustomEvent("updated", { detail: [this.x, this.y] }));
  }
}
const windowPoints = (points) => {
  if (points.length === 0) {
    return [
      [0, 1],
      [1, 1]
    ];
  }
  if (points.length === 1) {
    const [, y] = points[0];
    return [
      [0, y],
      [1, y]
    ];
  }
  const head = points[0];
  const tail = points[points.length - 1];
  return [[head[0], 0], ...points, [tail[0], 0]];
};
const pointsToWindowedPoints = (points) => windowPoints(points.map(({ x, y }) => [x, y]));
class Points {
  constructor() {
    __publicField(this, "_points", []);
    __publicField(this, "eventTarget", new EventTarget());
  }
  get points() {
    return [...this._points];
  }
  addPoint(x, y) {
    const pointToAdd = this.createPoint(x, y);
    this.dispatchUpdatedEvent();
    return pointToAdd;
  }
  addPoints(points) {
    const pointsMade = points.map(([x, y]) => this.createPoint(x, y));
    return pointsMade;
  }
  setPoints(points) {
    [...this._points].forEach((point) => this.deletePoint(point));
    return this.addPoints(points);
  }
  removePoint(point) {
    this.deletePoint(point);
    this.dispatchUpdatedEvent();
  }
  dispatchUpdatedEvent() {
    this.eventTarget.dispatchEvent(new CustomEvent("updated", { detail: this._points }));
  }
  createPoint(x, y) {
    const pointToAdd = new Point(x, y);
    pointToAdd.eventTarget.addEventListener("updated", () => {
      this._points.sort((a, b) => a.x - b.x);
      this.dispatchUpdatedEvent();
    });
    this._points.push(pointToAdd);
    this._points.sort((a, b) => a.x - b.x);
    return pointToAdd;
  }
  deletePoint(point) {
    this._points = this._points.filter((p) => p !== point);
  }
}
const drawChart = (ctx, area, values, style = {
  lineWidth: 1,
  strokeStyle: "#000",
  fillStyle: void 0,
  clip: false
}) => {
  const verticalScale = area[3];
  const horizontalScale = area[2] / (values.length - 1);
  const offset = verticalScale + area[1];
  ctx.lineWidth = style.lineWidth;
  ctx.strokeStyle = style.strokeStyle;
  ctx.beginPath();
  ctx.moveTo(area[0], area[1] + area[3]);
  for (let index = 0; index < values.length; index++) {
    ctx.lineTo(area[0] + index * horizontalScale, Math.max(area[1], offset - values[index] * verticalScale));
  }
  if (style.fillStyle) {
    ctx.fillStyle = style.fillStyle;
    ctx.lineTo(area[0] + area[2], area[1] + area[3]);
    if (style.clip) {
      ctx.clip();
      return;
    }
    ctx.fill();
  }
  ctx.stroke();
};
const CANVAS_HEIGHT = 1;
const updateColorCanvas = (colorTransferFunction, width, renderedDataRange, canvas) => {
  const workCanvas = canvas || document.createElement("canvas");
  workCanvas.setAttribute("width", String(width));
  workCanvas.setAttribute("height", String(CANVAS_HEIGHT));
  if (colorTransferFunction.getSize() === 0)
    return workCanvas;
  const rgba = colorTransferFunction.getUint8Table(renderedDataRange[0], renderedDataRange[1], width, true);
  const ctx = workCanvas.getContext("2d");
  if (ctx) {
    const pixelsArea = ctx.getImageData(0, 0, width, CANVAS_HEIGHT);
    for (let lineIdx = 0; lineIdx < CANVAS_HEIGHT; lineIdx++) {
      pixelsArea.data.set(rgba, lineIdx * 4 * width);
    }
    const nbValues = CANVAS_HEIGHT * width * 4;
    for (let i = 3; i < nbValues; i += 4) {
      pixelsArea.data[i] = 255;
    }
    ctx.putImageData(pixelsArea, 0, 0);
  }
  return workCanvas;
};
const windowPointsForSort = (points) => {
  const windowedPoints = windowPoints(points);
  windowedPoints[0][0] -= 1e-8;
  windowedPoints[windowedPoints.length - 1][0] += 1e-8;
  return windowedPoints;
};
const logTransform = (histogram) => {
  if (!histogram)
    return [];
  const loged = histogram.map((v) => v === 0 ? 0 : Math.log(v));
  const noZeros = loged.filter(Boolean);
  const min = Math.min(...noZeros);
  const max = Math.max(...noZeros);
  const delta = max - min;
  const normalized = loged.map((v) => v === 0 ? 0 : (v - min) / delta);
  return normalized;
};
function arrayEquals(a, b) {
  if (a.length !== b.length)
    return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i])
      return false;
  }
  return true;
}
const PADDING = 10;
const makeSvg = () => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("style", "position: absolute; top: 0; left: 0; z-index: 2; box-sizing: border-box; width: 100%; height: 100%;");
  return svg;
};
const Container = (parent) => {
  const root = document.createElement("div");
  root.setAttribute("style", "position: relative; width: 100%; height: 100%");
  parent.appendChild(root);
  const svg = makeSvg();
  root.appendChild(svg);
  const sizeEmitter = new EventTarget();
  const addSizeObserver = (cb) => {
    sizeEmitter.addEventListener("sizeupdated", cb);
  };
  const resizeObserver = new ResizeObserver(() => {
    sizeEmitter.dispatchEvent(new Event("sizeupdated"));
  });
  resizeObserver.observe(root);
  const paddedBorder = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  svg.appendChild(paddedBorder);
  paddedBorder.setAttribute("fill", "none");
  paddedBorder.setAttribute("stroke", "black");
  const appendChild = (shape) => {
    svg.appendChild(shape);
  };
  const removeChild = (shape) => {
    svg.removeChild(shape);
  };
  let viewBox = [0, 1, 0, 1];
  const getViewBox = () => viewBox;
  const setViewBox = (valueStart, valueEnd, opacityMin = 0, opacityMax = 1) => {
    const oldViewBox = viewBox;
    viewBox = [valueStart, valueEnd, opacityMin, opacityMax];
    if (!arrayEquals(oldViewBox, viewBox)) {
      sizeEmitter.dispatchEvent(new Event("sizeupdated"));
    }
  };
  const getSize = () => {
    const { top, left, width, height } = root.getBoundingClientRect();
    return {
      width: width - 2 * PADDING,
      height: height - 2 * PADDING,
      top: top + PADDING,
      left: left + PADDING
    };
  };
  const domToNormalized = (x, y) => {
    const { top, left, width, height } = getSize();
    const valueRange = viewBox[1] - viewBox[0] || 1e-3;
    const opacityRange = viewBox[3] - viewBox[2] || 1e-3;
    return [
      (x - left) / width * valueRange + viewBox[0],
      (1 - (y - top) / height) * opacityRange + viewBox[2]
    ];
  };
  const normalizedToSvg = (x, y) => {
    const { width, height } = getSize();
    const valueRange = viewBox[1] - viewBox[0] || 1e-3;
    const xSvg = (x - viewBox[0]) / valueRange * width + PADDING;
    const opacityRange = viewBox[3] - viewBox[2] || 1e-3;
    const ySvg = (1 - (y - viewBox[2]) / opacityRange) * height + PADDING;
    return [xSvg, ySvg];
  };
  const borderSize = () => {
    const [left, bottom] = normalizedToSvg(0, 0);
    const [right, top] = normalizedToSvg(1, 1);
    return { left, bottom, right, top };
  };
  const updateBorder = () => {
    const { left, bottom, right, top } = borderSize();
    paddedBorder.setAttribute("x", `${left}`);
    paddedBorder.setAttribute("y", `${top}`);
    paddedBorder.setAttribute("width", `${Math.max(0, right - left)}`);
    paddedBorder.setAttribute("height", `${Math.max(0, bottom - top)}`);
  };
  addSizeObserver(updateBorder);
  const remove = () => parent.removeChild(root);
  return {
    appendChild,
    removeChild,
    addSizeObserver,
    getViewBox,
    setViewBox,
    domToNormalized,
    normalizedToSvg,
    borderSize,
    remove,
    root
  };
};
const CONTROL_POINT_CLASS = "controlPoint";
const makeCircle = () => {
  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("r", "9");
  circle.setAttribute("fill", "white");
  circle.setAttribute("stroke", "black");
  circle.setAttribute("stroke-width", "2");
  circle.setAttribute("class", CONTROL_POINT_CLASS);
  circle.setAttribute("style", "cursor: move;");
  return circle;
};
class ControlPoint {
  constructor(container, point, deleteEventCallback, isNewPointFromPointer = false) {
    __publicField(this, "element");
    __publicField(this, "container");
    __publicField(this, "isDragging", false);
    __publicField(this, "isHovered", false);
    __publicField(this, "point");
    __publicField(this, "DELETE_EVENT", "deleteme");
    __publicField(this, "eventTarget", new EventTarget());
    this.element = makeCircle();
    this.point = point;
    this.container = container;
    container.addSizeObserver(() => {
      this.positionElement();
    });
    if (deleteEventCallback) {
      this.eventTarget.addEventListener(this.DELETE_EVENT, (e) => {
        deleteEventCallback(e);
      });
    }
    container.appendChild(this.element);
    this.positionElement();
    this.setupInteraction();
    if (isNewPointFromPointer)
      this.startInteraction(true);
  }
  remove() {
    this.container.removeChild(this.element);
  }
  positionElement() {
    const { x, y } = this.point;
    const [xSvg, ySvg] = this.container.normalizedToSvg(x, y);
    this.element.setAttribute("cx", String(xSvg));
    this.element.setAttribute("cy", String(ySvg));
  }
  movePoint(e) {
    const [x, y] = this.container.domToNormalized(e.clientX, e.clientY);
    this.point.setPosition(x, y);
    this.positionElement();
  }
  startInteraction(forceDragging = false) {
    this.isDragging = forceDragging;
    if (!this.isDragging) {
      this.element.setAttribute("stroke", "red");
    }
    const onPointerMove = (e) => {
      this.isDragging = true;
      this.element.setAttribute("stroke", "black");
      this.movePoint(e);
    };
    document.addEventListener("pointermove", onPointerMove);
    const onPointerUp = () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      if (!this.isDragging) {
        const delEvent = new CustomEvent(this.DELETE_EVENT, { detail: this });
        this.eventTarget.dispatchEvent(delEvent);
      }
      if (!this.isHovered)
        this.element.setAttribute("stroke-width", "2");
      this.isDragging = false;
    };
    document.addEventListener("pointerup", onPointerUp);
  }
  setupInteraction() {
    this.element.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.startInteraction();
    });
    this.element.addEventListener("pointerenter", () => {
      this.isHovered = true;
      this.element.setAttribute("stroke-width", "4");
    });
    this.element.addEventListener("pointerleave", () => {
      this.isHovered = false;
      if (!this.isDragging) {
        this.element.setAttribute("stroke-width", "2");
      }
    });
  }
}
class PointsController {
  constructor(container, points) {
    __publicField(this, "container");
    __publicField(this, "points");
    __publicField(this, "onPointsUpdated");
    __publicField(this, "controlPoints", []);
    __publicField(this, "isNewPointFromPointer", false);
    this.container = container;
    this.points = points;
    const { root } = container;
    root.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    this.onPointsUpdated = () => this.updatePoints();
    this.points.eventTarget.addEventListener("updated", this.onPointsUpdated);
    this.updatePoints();
  }
  remove() {
    this.points.eventTarget.removeEventListener("updated", this.onPointsUpdated);
  }
  onPointerDown(event) {
    event.preventDefault();
    const [x, y] = this.container.domToNormalized(event.clientX, event.clientY);
    this.isNewPointFromPointer = true;
    this.points.addPoint(x, y);
    this.isNewPointFromPointer = false;
  }
  onControlPointDelete(event) {
    this.points.removePoint(event.detail.point);
  }
  updatePoints() {
    const orphans = this.controlPoints.filter((cp) => !this.points.points.find((point) => point === cp.point));
    orphans.forEach((cp) => cp.remove());
    this.controlPoints = this.controlPoints.filter((cp) => !orphans.includes(cp));
    const isPointInControlPoints = (point) => this.controlPoints.find((cp) => cp.point === point);
    const addNewControlPoint = (point) => this.controlPoints.push(new ControlPoint(this.container, point, (e) => this.onControlPointDelete(e), this.isNewPointFromPointer));
    this.points.points.filter((pointModel) => !isPointInControlPoints(pointModel)).forEach(addNewControlPoint);
  }
}
const createLine = () => {
  const line = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
  line.setAttribute("fill", "none");
  line.setAttribute("stroke", "black");
  line.setAttribute("stroke-width", "2");
  return line;
};
class Line {
  constructor(container, points) {
    __publicField(this, "points");
    __publicField(this, "container");
    __publicField(this, "onPointsUpdated");
    __publicField(this, "element");
    this.container = container;
    this.points = points;
    this.element = createLine();
    this.container.appendChild(this.element);
    this.onPointsUpdated = () => this.update();
    this.points.eventTarget.addEventListener("updated", this.onPointsUpdated);
    this.container.addSizeObserver(() => {
      this.update();
    });
    this.update();
  }
  remove() {
    this.points.eventTarget.removeEventListener("updated", this.onPointsUpdated);
  }
  update() {
    if (this.points.points.length === 0) {
      this.element.setAttribute("points", "");
      return;
    }
    const stringPoints = pointsToWindowedPoints(this.points.points).map(([x, y]) => this.container.normalizedToSvg(x, y)).map(([x, y]) => `${x},${y}`).join(" ");
    this.element.setAttribute("points", stringPoints);
  }
}
const SCALE_SENSITIVITY = 1.1;
const WheelZoom = (container) => {
  container.root.addEventListener("wheel", (e) => {
    const scaleFactor = e.deltaY > 0 ? SCALE_SENSITIVITY : 1 / SCALE_SENSITIVITY;
    const [targetX] = container.domToNormalized(e.clientX, e.clientY);
    const [left, right] = container.getViewBox();
    const newLeft = Math.max(0, left - Math.max(0, targetX - left) * (scaleFactor - 1));
    const newRight = Math.min(1, (right - left) * scaleFactor + newLeft);
    if (newLeft === left && newRight === right) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    container.setViewBox(newLeft, newRight);
  });
};
const HISTOGRAM_COLOR = "rgba(50, 50, 50, 0.3)";
const Background = (container, points) => {
  const canvas = document.createElement("canvas");
  container.root.appendChild(canvas);
  canvas.setAttribute("style", "width: 100%; height: 100%; ");
  const ctx = canvas.getContext("2d");
  let colorTransferFunction;
  let histogram;
  const colorCanvas = document.createElement("canvas");
  const render = () => {
    if (!ctx)
      return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (colorTransferFunction) {
      const { width, height } = container.root.getBoundingClientRect();
      canvas.setAttribute("width", String(width));
      canvas.setAttribute("height", String(height));
      const { left, right, bottom, top } = container.borderSize();
      const borderWidth = Math.ceil(right - left);
      if (borderWidth < 0)
        return;
      const windowed = pointsToWindowedPoints(points.points);
      const linePoints = [[0, 0], ...windowed, [1, 0]].map(([x, y]) => container.normalizedToSvg(x, y));
      ctx.save();
      ctx.beginPath();
      linePoints.forEach(([x, y]) => {
        ctx.lineTo(x, y);
      });
      ctx.clip();
      const [headX] = linePoints[1];
      const [tailX] = linePoints[linePoints.length - 2];
      const headXClamped = Math.min(width, Math.max(0, headX));
      const tailXClamped = Math.min(width, Math.max(0, tailX));
      const colorCanvasWidth = Math.ceil(tailXClamped - headXClamped);
      if (colorCanvasWidth) {
        const pointPixelWidth = tailX - headX;
        const headClampAmount = (headXClamped - headX) / pointPixelWidth;
        const tailClampAmount = (tailXClamped - tailX) / pointPixelWidth;
        const dataRange = colorTransferFunction.getMappingRange();
        const dataWidth = dataRange[1] - dataRange[0];
        const visibleDataRange = [
          dataRange[0] + dataWidth * headClampAmount,
          dataRange[1] + dataWidth * tailClampAmount
        ];
        updateColorCanvas(colorTransferFunction, colorCanvasWidth, visibleDataRange, colorCanvas);
        ctx.drawImage(colorCanvas, 0, 0, colorCanvas.width, colorCanvas.height, Math.floor(headXClamped), Math.floor(top), colorCanvasWidth, Math.ceil(bottom - top));
      }
      ctx.restore();
    }
    if (histogram) {
      const { left, right, bottom, top } = container.borderSize();
      const graphArea = [left, top, right - left, bottom - top];
      drawChart(ctx, graphArea, histogram, {
        lineWidth: 1,
        strokeStyle: HISTOGRAM_COLOR,
        fillStyle: HISTOGRAM_COLOR
      });
    }
  };
  container.addSizeObserver(render);
  points.eventTarget.addEventListener("updated", render);
  const setColorTransferFunction = (ctf) => {
    colorTransferFunction = ctf;
    render();
  };
  const setHistogram = (newHistogram) => {
    histogram = newHistogram;
    render();
  };
  return {
    container,
    canvas,
    setColorTransferFunction,
    setHistogram,
    render,
    remove: () => container.root.removeChild(canvas)
  };
};
class TransferFunctionEditor {
  constructor(root) {
    __publicField(this, "points");
    __publicField(this, "line");
    __publicField(this, "pointController");
    __publicField(this, "container");
    __publicField(this, "background");
    this.container = Container(root);
    WheelZoom(this.container);
    this.points = new Points();
    const startPoints = [
      [0, 0],
      [1, 1]
    ];
    this.points.setPoints(startPoints);
    this.background = Background(this.container, this.points);
    this.line = new Line(this.container, this.points);
    this.pointController = new PointsController(this.container, this.points);
  }
  remove() {
    this.background.remove();
    this.container.remove();
  }
  getPoints() {
    return this.points.points.map(({ x, y }) => [x, y]);
  }
  setPoints(points) {
    this.points.setPoints(points);
    this.pointController.updatePoints();
    this.line.update();
    this.background.render();
  }
  get eventTarget() {
    return this.points.eventTarget;
  }
  setViewBox(valueStart, valueEnd, opacityMin = 0, opacityMax = 1) {
    this.container.setViewBox(valueStart, valueEnd, opacityMin, opacityMax);
  }
  setColorTransferFunction(ctf) {
    this.background.setColorTransferFunction(ctf);
  }
  setHistogram(histogram) {
    this.background.setHistogram(logTransform(histogram));
  }
}
export { TransferFunctionEditor, windowPointsForSort };
